import { isTransactionInDateRange, reportCategoryId, reportTransactionType } from '../../utils/reportTransactions';
import React, { useState, useMemo, useEffect } from 'react';
import { useData } from '../../contexts/DataContext';
import {
  Dialog,
  DialogContent,
  Box,
  Stack,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TablePagination,
  TableSortLabel,
  LinearProgress,
  useTheme,
  useMediaQuery,
  styled,
} from '@mui/material';
import { DialogHeader, DialogSummaryBar } from './AppDialog';
import CategoryChip from './CategoryChip';

/*
  TransactionListDialog: the single dialog for "show me the transactions behind
  this number" — used from the Analysis matrix (a single category) and from the
  Budget progress cards (a single category or an Income/Expense aggregate).

  Layout approach: the browser does all the measuring. The table uses the
  default auto layout, so single-line columns (Date, Account, Amount) are
  exactly as wide as their widest value, and Description/Notes wrap to absorb
  whatever is left. The dialog paper is width: fit-content, so the window hugs
  the table snugly — no dead space, nothing clipped — bounded to the viewport
  so long descriptions wrap instead of pushing columns off-screen.

  (Earlier versions estimated column widths in px from character counts; that
  broke on different fonts/zoom levels and clipped the Amount column. Don't
  reintroduce fixed pixel widths here.)

  Props: open, onClose, categoryId, aggregateType ('Income' | 'Expense'),
  dateRange ({ startDate, endDate }), title (optional override),
  budgetInfo (optional — renders a budget panel above the table; see
  BudgetPanel below for the shape).

  categoryId also accepts the sentinel 'uncategorized' to list transactions
  with no category (used by budget cards for uncategorised budgets).
*/

const Cell = styled(TableCell)(({ theme }) => ({
  fontWeight: 500,
  padding: theme.spacing(0.75, 1.25),
  whiteSpace: 'nowrap',
  fontSize: '0.78rem',
  lineHeight: 1.3,
  verticalAlign: 'top',
}));

const COLUMN_META = {
  date: { label: 'Date', align: 'left' },
  // Description wraps onto extra lines rather than truncating, so long
  // descriptions stay readable without forcing a wide column. minWidth keeps
  // it usable when the other columns are hungry.
  description: { label: 'Description', align: 'left', sx: { whiteSpace: 'normal', overflowWrap: 'anywhere', minWidth: 180 } },
  category: { label: 'Category', align: 'left' },
  account: { label: 'Account', align: 'left' },
  amount: { label: 'Amount', align: 'right' },
  notes: { label: 'Notes', align: 'left', sx: { whiteSpace: 'normal', overflowWrap: 'anywhere', minWidth: 120, color: 'text.secondary' } },
};

/*
  BudgetPanel: budget context rendered above the transaction table when the
  dialog is opened from a budget card. Shows the overall progress bar
  (spent / total, % used or % over, amount left) and — when several budgets
  contribute to the category — a compact row per budget so it's clear what
  makes up the total.

  budgetInfo shape: {
    spent, budgetAmount, remaining, percentage, isOverBudget,
    color,                       // category colour for the bar
    budgets: [budget status]     // getBudgetStatus results, one per budget
  }
*/
const budgetPeriodLabel = (budget) => {
  if (!budget?.isRecurring) return 'One-off';
  const period = budget.recurringPeriod || 'monthly';
  return period.charAt(0).toUpperCase() + period.slice(1);
};

const BudgetPanel = ({ budgetInfo }) => {
  const { spent, budgetAmount, remaining, percentage, isOverBudget, color, budgets = [] } = budgetInfo;
  const fmt = (amount) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0,
    }).format(amount);
  const barColor = isOverBudget ? 'error.main' : color || 'success.main';

  return (
    <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1, mb: 0.75 }}>
        <Typography variant="body2">
          <Box component="span" sx={{ fontWeight: 700 }}>{fmt(spent)}</Box>
          <Box component="span" sx={{ color: 'text.secondary' }}> of {fmt(budgetAmount)}</Box>
          {budgets.length === 1 && (
            <Box component="span" sx={{ color: 'text.secondary' }}> · {budgetPeriodLabel(budgets[0]?.budget)}</Box>
          )}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 700, color: isOverBudget ? 'error.main' : 'success.main', flexShrink: 0 }}>
          {isOverBudget
            ? `${Math.round(percentage - 100)}% over`
            : `${fmt(remaining)} left`}
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={Math.min(percentage, 100)}
        sx={{
          height: 8,
          borderRadius: 4,
          bgcolor: 'grey.200',
          '& .MuiLinearProgress-bar': { bgcolor: barColor },
        }}
      />
      {/* Several budgets make up the total — list what each contributes.
          Spending is tracked per category (not per budget), so only the
          budgeted amounts are itemised here. */}
      {budgets.length > 1 && (
        <Stack spacing={0.25} sx={{ mt: 1.25 }}>
          {budgets.map((status, i) => (
            <Box key={status.budget?.id || i} sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
              <Typography variant="body3" noWrap sx={{ flex: 1, minWidth: 0 }}>
                {status.budget?.name || 'Budget'}
                <Box component="span" sx={{ color: 'text.secondary' }}> · {budgetPeriodLabel(status.budget)}</Box>
              </Typography>
              <Typography variant="body3" sx={{ flexShrink: 0, fontWeight: 700 }}>
                {fmt(status.budgetAmount)}
              </Typography>
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
};

const AmountText = ({ amount, formatCurrency }) => (
  <Box component="span" sx={{ color: amount < 0 ? 'error.main' : 'success.main', fontWeight: 700 }}>
    {amount < 0 ? '−' : '+'}{formatCurrency(amount)}
  </Box>
);

const TransactionListDialog = ({ open, onClose, categoryId, aggregateType, dateRange, title, budgetInfo, reportType }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { transactions, categories, accounts } = useData();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [sort, setSort] = useState({ key: 'date', dir: 'desc' });

  const filteredTransactions = useMemo(() => {
    let filtered = [];

    if (categoryId === 'uncategorized') {
      filtered = transactions.filter((txn) => reportCategoryId(txn, categories) === 'uncategorized');
    } else if (categoryId) {
      filtered = transactions.filter((txn) => reportCategoryId(txn, categories) === categoryId);
    } else if (aggregateType === 'Income') {
      filtered = transactions.filter((txn) => {
        const category = categories.find((c) => c.id === txn.category);
        return txn.amount > 0 && (!category || !category.isTransfer);
      });
    } else if (aggregateType === 'Expense') {
      filtered = transactions.filter((txn) => {
        const category = categories.find((c) => c.id === txn.category);
        return txn.amount < 0 && (!category || !category.isTransfer);
      });
    }

    if (reportType) filtered = filtered.filter(txn => reportTransactionType(txn, categories) === reportType);
    filtered = filtered.filter(txn => isTransactionInDateRange(txn, dateRange));

    return filtered;
  }, [transactions, categories, categoryId, aggregateType, dateRange, reportType]);

  // Reset pagination when the dialog is pointed at a different slice.
  useEffect(() => {
    setPage(0);
  }, [categoryId, aggregateType, dateRange]);

  const formatDate = (dateString) => new Date(dateString).toLocaleDateString();
  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Math.abs(amount));
  const getCategoryName = (id) => categories.find((cat) => cat.id === id)?.name || 'Uncategorized';
  const getCategoryColor = (id) => categories.find((cat) => cat.id === id)?.color;
  const getAccountName = (accountId) => accounts.find((acc) => acc.id === accountId)?.name || 'Unknown Account';

  const getSortValue = (key, txn) => {
    switch (key) {
      case 'description': return (txn.description || '').toLowerCase();
      case 'category': return getCategoryName(txn.category).toLowerCase();
      case 'account': return getAccountName(txn.account).toLowerCase();
      case 'amount': return txn.amount;
      case 'notes': return (txn.notes || '').toLowerCase();
      case 'date':
      default: return new Date(txn.date).getTime();
    }
  };

  const sortedTransactions = useMemo(() => {
    const arr = [...filteredTransactions];
    arr.sort((a, b) => {
      const va = getSortValue(sort.key, a);
      const vb = getSortValue(sort.key, b);
      if (va < vb) return sort.dir === 'asc' ? -1 : 1;
      if (va > vb) return sort.dir === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
    // getSortValue closes over categories/accounts, which are in the dep list
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredTransactions, sort, categories, accounts]);

  const paginatedTransactions = useMemo(
    () => sortedTransactions.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sortedTransactions, page, rowsPerPage]
  );

  const handleSort = (key) => {
    setSort((prev) => (prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));
    setPage(0);
  };

  const renderCell = (key, transaction) => {
    switch (key) {
      case 'date': return formatDate(transaction.date);
      case 'description': return transaction.description;
      case 'category':
        return (
          <CategoryChip
            color={getCategoryColor(transaction.category)}
            label={getCategoryName(transaction.category)}
            size="small"
            sx={{ height: 20, fontSize: '0.75rem', maxWidth: '100%' }}
          />
        );
      case 'account': return getAccountName(transaction.account);
      case 'amount': return <AmountText amount={transaction.amount} formatCurrency={formatCurrency} />;
      case 'notes': return transaction.notes || '—';
      default: return null;
    }
  };

  const totalAmount = useMemo(
    () => filteredTransactions.reduce((sum, txn) => sum + txn.amount, 0),
    [filteredTransactions]
  );

  // Drilled into a single category → the Category column would just repeat the
  // title, so only show it for the multi-category aggregate views.
  const showCategoryColumn = !categoryId;

  // The Notes column is usually empty — only show it when something has notes,
  // so the table doesn't carry a column of em-dashes.
  const showNotesColumn = filteredTransactions.some((t) => t.notes);

  const dialogTitle =
    title ||
    (categoryId === 'uncategorized'
      ? 'Uncategorized Transactions'
      : categoryId
      ? `${getCategoryName(categoryId)} Transactions`
      : aggregateType
      ? `${aggregateType} Transactions`
      : 'Transactions');

  const count = filteredTransactions.length;
  const isEmpty = count === 0;

  const visibleColumns = [
    'date',
    'description',
    ...(showCategoryColumn ? ['category'] : []),
    'account',
    'amount',
    ...(showNotesColumn ? ['notes'] : []),
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          // Hug the table's natural width (browser-measured, so nothing is ever
          // clipped), bounded to the viewport so long descriptions wrap instead
          // of pushing the Amount column off-screen. Height hugs the rows too —
          // pagination sits right under the table, no dead space.
          width: isMobile ? '100%' : 'fit-content',
          minWidth: isMobile ? '100%' : 480,
          maxWidth: isMobile ? '100%' : 'min(1100px, 95vw)',
          height: isMobile ? '100%' : 'auto',
          maxHeight: isMobile ? '100%' : '90vh',
          overflow: 'hidden',
        },
      }}
    >
      <DialogHeader title={dialogTitle} onClose={onClose} />

      <DialogSummaryBar
        left={
          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>
            {count} transaction{count !== 1 ? 's' : ''}
          </Typography>
        }
        right={
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>
              Total
            </Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: totalAmount < 0 ? 'error.main' : 'success.main' }}>
              {totalAmount < 0 ? '−' : '+'}{formatCurrency(totalAmount)}
            </Typography>
          </Box>
        }
      />

      {budgetInfo && <BudgetPanel budgetInfo={budgetInfo} />}

      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
        {isEmpty ? (
          <Box sx={{ py: 6, px: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No transactions found for this selection.
            </Typography>
          </Box>
        ) : isMobile ? (
          <Box sx={{ flex: 1, overflow: 'auto', p: 1.5, bgcolor: 'background.default' }}>
            <Stack spacing={1}>
              {paginatedTransactions.map((transaction) => {
                const accent = getCategoryColor(transaction.category)
                  || (transaction.amount > 0 ? theme.palette.success.main : theme.palette.error.main);
                return (
                  <Box
                    key={transaction._id || transaction.id}
                    sx={{
                      position: 'relative',
                      bgcolor: 'background.paper',
                      borderRadius: 1.5,
                      border: '1px solid',
                      borderColor: 'divider',
                      pl: 1.75,
                      pr: 1.25,
                      py: 0.9,
                      overflow: 'hidden',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: 4,
                        backgroundColor: accent,
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1.25 }}>
                      <Typography noWrap variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.85rem', minWidth: 0, flex: 1 }}>
                        {transaction.description || 'Untitled'}
                      </Typography>
                      <Typography variant="subtitle2" sx={{ flexShrink: 0, whiteSpace: 'nowrap', fontSize: '0.85rem' }}>
                        <AmountText amount={transaction.amount} formatCurrency={formatCurrency} />
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.75, mt: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                        {formatDate(transaction.date)}
                      </Typography>
                      <CategoryChip
                        color={getCategoryColor(transaction.category)}
                        label={getCategoryName(transaction.category)}
                        size="small"
                        sx={{ height: 20, fontSize: '0.75rem' }}
                      />
                      <Typography variant="caption" color="text.secondary" noWrap sx={{ minWidth: 0 }}>
                        {getAccountName(transaction.account)}
                      </Typography>
                    </Box>
                    {transaction.notes && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, fontStyle: 'italic' }}>
                        {transaction.notes}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Stack>
          </Box>
        ) : (
          <TableContainer
            sx={{
              // Edge-to-edge: no margins or borders here — the dialog's
              // fit-content width is measured from the table, and any extra
              // chrome around the scroller is what used to clip the Amount
              // column. The first/last cell padding provides the gutters.
              flex: '1 1 auto',
              minHeight: 0,
              overflow: 'auto',
              // Slim scrollbars for when the row count exceeds the 90vh cap
              '&::-webkit-scrollbar': { width: 11, height: 11 },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(0,0,0,0.28)',
                borderRadius: 8,
                border: '2px solid transparent',
                backgroundClip: 'content-box',
              },
              '&::-webkit-scrollbar-thumb:hover': { backgroundColor: 'rgba(0,0,0,0.45)' },
              '&::-webkit-scrollbar-track': { backgroundColor: 'rgba(0,0,0,0.04)' },
              scrollbarWidth: 'thin',
            }}
          >
            {/* Auto table layout: the browser sizes each column to its content,
                so nothing is clipped; Description/Notes wrap to take the slack. */}
            <Table
              size="small"
              stickyHeader
              sx={{
                width: '100%',
                // Side gutters live on the outer cells so they're part of the
                // table's own measured width (margins on the scroller aren't).
                '& td:first-of-type, & th:first-of-type': { pl: 2.5 },
                '& td:last-of-type, & th:last-of-type': { pr: 2.5 },
              }}
            >
              <TableHead>
                <TableRow>
                  {visibleColumns.map((key) => {
                    const meta = COLUMN_META[key];
                    const active = sort.key === key;
                    return (
                      <Cell
                        key={key}
                        align={meta.align}
                        sortDirection={active ? sort.dir : false}
                        sx={{ userSelect: 'none', bgcolor: 'background.paper', whiteSpace: 'nowrap' }}
                      >
                        <TableSortLabel
                          active={active}
                          direction={active ? sort.dir : 'asc'}
                          onClick={() => handleSort(key)}
                        >
                          {meta.label}
                        </TableSortLabel>
                      </Cell>
                    );
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedTransactions.map((transaction) => (
                  <TableRow key={transaction._id || transaction.id} hover>
                    {visibleColumns.map((key) => (
                      <Cell key={key} align={COLUMN_META[key].align} sx={COLUMN_META[key].sx}>
                        {renderCell(key, transaction)}
                      </Cell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {!isEmpty && (
          <Box sx={{ borderTop: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
            <TablePagination
              component="div"
              count={count}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25, 50]}
              sx={{
                '& .MuiTablePagination-toolbar': { minHeight: 44 },
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: '0.8125rem' },
              }}
            />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TransactionListDialog;

